import { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { CalendarRange, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { api, money, shortDate } from "../lib/api";

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const computeRange = (preset) => {
  const today = new Date();
  const end = isoDate(today);
  if (preset === "week") { const d = new Date(today); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return { start: isoDate(d), end }; }
  if (preset === "month") return { start: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), end };
  if (preset === "year") return { start: isoDate(new Date(today.getFullYear(), 0, 1)), end };
  return { start: "", end: "" };
};

const PRESETS = [
  { v: "all", l: "All time" },
  { v: "week", l: "This week" },
  { v: "month", l: "This month" },
  { v: "year", l: "This year" },
  { v: "custom", l: "Custom" },
];

const TX_CHIPS = [
  { v: "all", l: "All" },
  { v: "orders", l: "Orders" },
  { v: "invoices", l: "Invoices" },
  { v: "payments", l: "Payments" },
  { v: "credit_notes", l: "Credit notes" },
  { v: "refunds", l: "Refunds" },
];

const TX_GROUPS = {
  all: null,
  orders: ["Sales Order", "Purchase Order"],
  invoices: ["Invoice"],
  payments: ["Payment"],
  credit_notes: ["Credit Note"],
  refunds: ["Refund"],
};

const pdfMoney = (value, currency = "INR") => {
  const rate = currency === "USD" ? 1 / 83 : 1;
  return new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", { style: "currency", currency, currencyDisplay: "code", maximumFractionDigits: 0 }).format((value || 0) * rate);
};
const csvCell = (v) => { const t = v == null ? "" : String(v); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };

export default function Reports({ currency }) {
  const [preset, setPreset] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [txFilter, setTxFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    api.get("/reports/summary", { params })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load reports"))
      .finally(() => setLoading(false));
  }, [start, end]);

  const selectPreset = (p) => {
    setPreset(p);
    if (p === "custom") {
      if (!start && !end) { const r = computeRange("month"); setStart(r.start); setEnd(r.end); }
      return;
    }
    const r = computeRange(p);
    setStart(r.start);
    setEnd(r.end);
  };

  const rangeLabel = useMemo(() => (!start && !end ? "All time" : `${start ? shortDate(start) : "Start"} – ${end ? shortDate(end) : "Today"}`), [start, end]);

  const exportCsv = () => {
    if (!data) return;
    const { pnl, cash_flow: cash, tax, inventory } = data;
    const rows = [
      ["FlowSphere System — Financial Report"],
      ["Period", rangeLabel],
      ["Generated", new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })],
      [],
      ["Profit & Loss"],
      ["Sales revenue", pnl.revenue],
      ["Cost of goods sold", -pnl.cogs],
      ["Gross profit", pnl.gross_profit],
      ["Operating expenses", -pnl.operating_expenses],
      ["Net profit (before tax)", pnl.net_profit],
      ["Tax payable (GST collected)", -pnl.tax_collected],
      ["Net profit after tax", pnl.net_profit_after_tax],
      [],
      ["Expenses by category"],
      ...Object.entries(pnl.expenses_by_category).map(([k, v]) => [k, v]),
      [],
      ["Cash flow"],
      ["Cash in from customers", cash.inflow],
      ["Cash paid out (expenses & refunds)", -cash.outflow],
      ["Net cash movement", cash.net],
      [],
      ["Tax summary"],
      ...Object.entries(tax.breakdown).map(([k, v]) => [`GST ${k}`, v]),
      ["Total tax collected", tax.total],
      [],
      ["Inventory valuation (current)"],
      ["Product", "SKU", "Stock", "Unit cost", "Value"],
      ...inventory.items.map((i) => [i.name, i.sku, i.stock, i.cost, i.value]),
      ["Total inventory value", "", "", "", inventory.total],
      [],
      ["Transactions"],
      ["Date", "Type", "Reference", "Party", "Status", "Amount"],
      ...(data.transactions || []).map((t) => [t.date, t.type, t.reference, t.party, t.status, t.amount]),
    ];
    const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `flowsphere-report-${start || "all"}-${end || "today"}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success("Report exported to CSV");
  };

  const exportPdf = () => {
    if (!data) return;
    const { pnl, cash_flow: cash, tax, inventory } = data;
    const m = (v) => pdfMoney(v, currency);
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    doc.setFontSize(18);
    doc.text("FlowSphere System", 40, 48);
    doc.setFontSize(11);
    doc.setTextColor(120);
    doc.text("Financial Report", 40, 66);
    doc.setFontSize(10);
    doc.text(`Period: ${rangeLabel}`, 40, 84);
    doc.text(`Generated: ${new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}`, 40, 98);

    autoTable(doc, {
      startY: 120,
      head: [["Profit & Loss", "Amount"]],
      body: [
        ["Sales revenue", m(pnl.revenue)],
        ["Cost of goods sold", `- ${m(pnl.cogs)}`],
        ["Gross profit", m(pnl.gross_profit)],
        ["Operating expenses", `- ${m(pnl.operating_expenses)}`],
        ["Net profit (before tax)", m(pnl.net_profit)],
        ["Tax payable (GST collected)", `- ${m(pnl.tax_collected)}`],
        ["Net profit after tax", m(pnl.net_profit_after_tax)],
      ],
      theme: "striped",
      headStyles: { fillColor: [10, 22, 40] },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 18,
      head: [["Cash flow", "Amount"]],
      body: [
        ["Cash in from customers", m(cash.inflow)],
        ["Cash paid out (expenses & refunds)", `- ${m(cash.outflow)}`],
        ["Net cash movement", m(cash.net)],
      ],
      theme: "striped",
      headStyles: { fillColor: [10, 22, 40] },
    });

    const taxRows = Object.entries(tax.breakdown).map(([k, v]) => [`GST ${k}`, m(v)]);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 18,
      head: [["Tax summary", "Amount"]],
      body: taxRows.length ? [...taxRows, ["Total tax collected", m(tax.total)]] : [["No confirmed invoices in this period", ""]],
      theme: "striped",
      headStyles: { fillColor: [10, 22, 40] },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 18,
      head: [["Product", "SKU", "Stock", "Unit cost", "Value"]],
      body: [
        ...inventory.items.map((i) => [i.name, i.sku || "-", String(i.stock), m(i.cost), m(i.value)]),
        ["Total inventory value", "", "", "", m(inventory.total)],
      ],
      theme: "striped",
      headStyles: { fillColor: [10, 22, 40] },
    });

    const txRows = (data.transactions || []).map((t) => [shortDate(t.date), t.type, t.reference || "-", t.party || "-", t.status || "", m(t.amount)]);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 18,
      head: [["Date", "Type", "Reference", "Party", "Status", "Amount"]],
      body: txRows.length ? txRows : [["No transactions in this period", "", "", "", "", ""]],
      theme: "striped",
      headStyles: { fillColor: [10, 22, 40] },
    });

    doc.save(`flowsphere-report-${start || "all"}-${end || "today"}.pdf`);
    toast.success("Report exported to PDF");
  };

  return (
    <div className="reports-wrap" data-testid="reports-view">
      <div className="panel report-controls">
        <div className="report-presets" data-testid="report-presets">
          <CalendarRange size={16} className="report-controls-icon" />
          {PRESETS.map((p) => (
            <button key={p.v} data-testid={`report-preset-${p.v}`} className={`preset-pill ${preset === p.v ? "active" : ""}`} onClick={() => selectPreset(p.v)}>
              {p.l}
            </button>
          ))}
          {preset === "custom" && (
            <div className="report-custom-dates">
              <input type="date" data-testid="report-start-input" value={start} onChange={(e) => setStart(e.target.value)} />
              <span>to</span>
              <input type="date" data-testid="report-end-input" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
          )}
        </div>
        <div className="report-export-actions">
          <span className="report-range-label" data-testid="report-range-label">{rangeLabel}</span>
          <button className="secondary-button" data-testid="export-report-csv" onClick={exportCsv} disabled={!data}><Download size={15} /> CSV</button>
          <button className="primary-button" data-testid="export-report-pdf" onClick={exportPdf} disabled={!data}><FileText size={15} /> PDF</button>
        </div>
      </div>

      {loading || !data ? (
        <div className="skeleton-grid"><div /><div /><div /></div>
      ) : (
        <>
          {data.trend.length > 0 && (
            <section className="panel report-chart-panel" data-testid="report-trend">
              <div className="panel-heading"><div><h3>Revenue vs expenses</h3><p>Trend across the selected period</p></div></div>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={data.trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,140,170,.18)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={70} tickFormatter={(v) => money(v, currency)} />
                    <Tooltip formatter={(v) => money(v, currency)} />
                    <Legend />
                    <Bar dataKey="revenue" name="Revenue" fill="#2FB6E4" radius={[5, 5, 0, 0]} />
                    <Bar dataKey="expenses" name="Expenses" fill="#e8a23d" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}

          <div className="reports-grid">
            <section className="panel report-panel" data-testid="report-pnl">
              <div className="panel-heading">
                <div><h3>Profit &amp; Loss</h3><p>Income minus expenses · {rangeLabel}</p></div>
                <span className={`status ${data.pnl.net_profit >= 0 ? "paid" : "overdue"}`}>{data.pnl.net_profit >= 0 ? "Profitable" : "Watch"}</span>
              </div>
              <ul className="report-lines">
                <li><span>Sales revenue</span><strong>{money(data.pnl.revenue, currency)}</strong></li>
                <li><span>Cost of goods sold</span><strong>−{money(data.pnl.cogs, currency)}</strong></li>
                <li className="divider"><span>Gross profit</span><strong>{money(data.pnl.gross_profit, currency)}</strong></li>
                <li><span>Operating expenses</span><strong>−{money(data.pnl.operating_expenses, currency)}</strong></li>
                <li><span>Net profit (before tax)</span><strong>{money(data.pnl.net_profit, currency)}</strong></li>
                <li><span>Tax payable (GST collected)</span><strong>−{money(data.pnl.tax_collected, currency)}</strong></li>
                <li className="totals"><span>Net profit after tax</span><strong>{money(data.pnl.net_profit_after_tax, currency)}</strong></li>
              </ul>
              <p className="report-note">Net profit after setting aside {money(data.pnl.tax_collected, currency)} GST collected for remittance.</p>
            </section>

            <section className="panel report-panel" data-testid="report-cash-flow">
              <div className="panel-heading"><div><h3>Cash flow</h3><p>Money in and money out</p></div></div>
              <ul className="report-lines">
                <li><span>Cash in from customers</span><strong>{money(data.cash_flow.inflow, currency)}</strong></li>
                <li><span>Cash paid out (expenses &amp; refunds)</span><strong>−{money(data.cash_flow.outflow, currency)}</strong></li>
                <li className="totals"><span>Net cash movement</span><strong>{money(data.cash_flow.net, currency)}</strong></li>
              </ul>
            </section>

            <section className="panel report-panel" data-testid="report-tax">
              <div className="panel-heading"><div><h3>Tax summary</h3><p>GST collected by rate</p></div></div>
              {Object.keys(data.tax.breakdown).length === 0 ? (
                <p className="report-note">No confirmed invoices in this period.</p>
              ) : (
                <ul className="report-lines">
                  {Object.entries(data.tax.breakdown).map(([rate, value]) => (
                    <li key={rate}><span>GST {rate}</span><strong>{money(value, currency)}</strong></li>
                  ))}
                  <li className="totals"><span>Total tax collected</span><strong>{money(data.tax.total, currency)}</strong></li>
                </ul>
              )}
            </section>

            <section className="panel report-panel wide" data-testid="report-inventory">
              <div className="panel-heading">
                <div><h3>Inventory valuation</h3><p>Stock-on-hand valued at cost (current)</p></div>
                <strong>{money(data.inventory.total, currency)}</strong>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Product</th><th>SKU</th><th>Stock</th><th>Unit cost</th><th>Value</th></tr></thead>
                  <tbody>
                    {data.inventory.items.map((item) => (
                      <tr key={item.id}>
                        <td><strong>{item.name}</strong></td>
                        <td className="mono">{item.sku || "—"}</td>
                        <td>{item.stock}{item.stock <= item.reorder_level && <span className="stock-alert reorder-inline">low</span>}</td>
                        <td>{money(item.cost, currency)}</td>
                        <td><strong>{money(item.value, currency)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <section className="panel report-panel wide" data-testid="report-transactions">
            <div className="panel-heading">
              <div><h3>Transactions</h3><p>Every order, invoice, payment, credit note &amp; refund · {rangeLabel}</p></div>
              <strong>{(data.transactions || []).length}</strong>
            </div>
            <div className="tx-filter-chips" data-testid="tx-filter-chips">
              {TX_CHIPS.map((c) => (
                <button key={c.v} data-testid={`tx-filter-${c.v}`} className={`preset-pill ${txFilter === c.v ? "active" : ""}`} onClick={() => setTxFilter(c.v)}>{c.l}</button>
              ))}
            </div>
            <div className="table-wrap">
              {(() => {
                const all = data.transactions || [];
                const rows = txFilter === "all" ? all : all.filter((t) => (TX_GROUPS[txFilter] || []).includes(t.type));
                if (rows.length === 0) return <p className="report-note">No transactions in this period.</p>;
                return (
                  <table data-testid="transactions-table">
                    <thead><tr><th>Date</th><th>Type</th><th>Reference</th><th>Party</th><th>Status</th><th className="right">Amount</th></tr></thead>
                    <tbody>
                      {rows.map((t, i) => (
                        <tr key={i} data-testid={`tx-row-${i}`}>
                          <td data-label="Date">{shortDate(t.date)}</td>
                          <td data-label="Type"><span className={`tx-type tx-${t.type.toLowerCase().replace(/\s+/g, "-")}`}>{t.type}</span></td>
                          <td data-label="Reference" className="mono">{t.reference || "—"}</td>
                          <td data-label="Party">{t.party || "—"}</td>
                          <td data-label="Status"><small>{t.status || "—"}</small></td>
                          <td data-label="Amount" className="right"><strong>{money(t.amount, currency)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
